import { oktaAuth } from '~/config/oktaConfig';
import log from '~/logger';
import { buildCpsFilterQuery } from '~/utils/cpsFilters';
const API_BASE_URLS = {
    'pulse-bff': process.env.REACT_APP_PULSE_BFF_API_BASE_URL ?? '',
    'workspace-services': process.env.REACT_APP_WORKSPACE_API_BASE_URL ?? '',
    'pulse-projects': process.env.REACT_APP_PULSE_PROJECTS_API_BASE_URL ?? ''
};
function buildUrl(endpoint, params) {
    if (!params)
        return endpoint;
    const parts = [];
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
        }
    }
    if (parts.length === 0)
        return endpoint;
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}${parts.join('&')}`;
}
class ApiClient {
    async get(endpoint, api, params, resourceName) {
        const url = buildUrl(endpoint, params);
        return this.request('GET', url, api, undefined, resourceName);
    }
    async post(endpoint, api, data, resourceName) {
        return this.request('POST', endpoint, api, data, resourceName);
    }
    async fetchResourceWithFilter(options) {
        const { endpoint, filters, sortParam, api, resourceName } = options;
        const filtersQuery = buildCpsFilterQuery(filters.filters);
        if (filtersQuery.length === 0) {
            throw new Error('At least one filter is required');
        }
        let url = `${endpoint}?${filtersQuery}`;
        if (sortParam) {
            url += `&${sortParam}`;
        }
        return this.get(url, api, undefined, resourceName);
    }
    async request(method, endpoint, api, data, resourceName) {
        const baseUrl = API_BASE_URLS[api];
        const url = `${baseUrl}${endpoint}`;
        const token = oktaAuth.getAccessToken();
        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: data ? JSON.stringify(data) : undefined
            });
            if (!response.ok) {
                await this.handleErrorResponse(response);
            }
            return response.json();
        }
        catch (error) {
            if (error instanceof Error && error.message === 'Failed to fetch') {
                const errorMsg = resourceName
                    ? `Failed to fetch ${resourceName}`
                    : 'Network request failed';
                log.error(errorMsg, { url, method, resourceName });
                throw new Error(errorMsg);
            }
            throw error;
        }
    }
    async handleErrorResponse(response) {
        let errorMessage = 'Request failed';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
        }
        catch {
            errorMessage = response.statusText || errorMessage;
        }
        log.error('API request failed', {
            status: response.status,
            message: errorMessage,
            url: response.url
        });
        if (response.status === 401) {
            throw new Error('Authentication failed. Please log in again.');
        }
        if (response.status === 400) {
            throw new Error(`Bad request: ${errorMessage}`);
        }
        if (response.status === 404) {
            throw new Error(`Resource not found: ${errorMessage}`);
        }
        if (response.status >= 500) {
            throw new Error('Server error occurred. Please try again later.');
        }
        throw new Error(`Request failed: ${errorMessage}`);
    }
}
export const apiClient = new ApiClient();
